import { PlanTrailActionContext, InsertMarkerData, InsertPointData, getMap, getLayerRegistry, MIN_SEGMENT_DISTANCE_METERS } from "./types";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { isClickOnWaypointHelper } from "../../../../../utils/helpers/IsClickOnWaypointHelper";
import { isClickOnPOIHelper } from "../../../../../utils/helpers/IsClickOnPOIHelper";
import { getElevationData } from "../../../../../utils/helpers/HiikerElevationApiHelper";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { AddMarkerCommand } from "../../undo/commands/AddMarkerCommand";
import { InsertMarkerCommand } from "../../undo/commands/InsertMarkerCommand";
import {
  lineString,
  lineSplit,
  point,
  nearestPointOnLine,
  cleanCoords,
  distance as turfDistance
} from "@turf/turf";
import mapboxgl from "mapbox-gl";
import { MapLayerCategory } from "../../../../../utils/layer-registry";

// Declare the global flag for context menu visibility
declare global {
  interface Window {
    mapContextMenuVisible?: boolean;
  }
}

export const mapInteractionsActions = {
  async addMarkerAtCoordinates({ state, commit, dispatch }: PlanTrailActionContext, coordinates: { lng: number; lat: number }) {
    console.log('[ADD_MARKER] addMarkerAtCoordinates called', coordinates);
    console.log('[ADD_MARKER] activeRouteKey:', state.activeRouteKey);
    console.log('[ADD_MARKER] routes:', Object.keys(state.routes));

    // Ensure there is a route to add the marker to. A fresh planner starts with
    // no routes, and "Add trace" only renders a tracer line (it creates no
    // route) — so the first map click would otherwise be dropped with
    // "No active route found", making it impossible to start drawing / tracing.
    if (!state.activeRouteKey || !state.routes[state.activeRouteKey]) {
      commit('createNewRoute');
    }

    // Work with the active route directly
    const activeRoute = state.routes[state.activeRouteKey];
    console.log('[ADD_MARKER] activeRoute exists:', !!activeRoute);

    if (!activeRoute) {
      console.error('[ADD_MARKER] No active route found!');
      return;
    }

    // Skip creating a zero-length segment if the new point is effectively identical to the last marker
    if (activeRoute.markers.length > 0) {
      const lastMarker = activeRoute.markers[activeRoute.markers.length - 1];
      const distanceToLast = turfDistance(
        [lastMarker.markerGeometry.coordinates[0], lastMarker.markerGeometry.coordinates[1]],
        [coordinates.lng, coordinates.lat],
        { units: 'meters' }
      );

      if (distanceToLast < MIN_SEGMENT_DISTANCE_METERS) {
        console.warn(`[ADD_MARKER] Skipping marker – too close to previous (${distanceToLast.toFixed(2)}m < ${MIN_SEGMENT_DISTANCE_METERS}m)`);
        return;
      }
    }

    const id = generateID();

    // Create the new marker
    const marker = new MarkerFeature(
        new MarkerGeometry([coordinates.lng, coordinates.lat]),
        new MarkerProperties(id, state.isSnap, Date.now().toString(), 0)
    );

    // Add marker to the end of the route
    const markerIndex = activeRoute.markers.length;
    activeRoute.markers.push(marker);
    console.log('[ADD_MARKER] Marker added, total markers:', activeRoute.markers.length);

    // Create linestring from the previous marker if there is one
    let provisional: LineStringFeature | null = null;
    if (activeRoute.markers.length >= 2) {
      const prevMarker = activeRoute.markers[activeRoute.markers.length - 2];
      provisional = await dispatch("createPolylineGeoJson", {
        startMarker: prevMarker,
        endMarker: marker
      });
      activeRoute.lineStrings.push(provisional);
    }

    // Push command to undo manager (stores only the marker + one linestring)
    state.undoManager.push(new AddMarkerCommand({
      routeKey: state.activeRouteKey!,
      markerIndex,
      marker,
      lineString: provisional,
    }));

    // Update route timestamp
    activeRoute.updated_at = new Date();

    // Refresh map display — update routes and markers only
    console.log('[ADD_MARKER] Calling updateRouteAndMarkers');
    await dispatch('updateRouteAndMarkers');
    console.log('[ADD_MARKER] updateRouteAndMarkers complete');
  },
  async mapClicked({ state, rootState, commit, dispatch, getters }: PlanTrailActionContext, e: mapboxgl.MapMouseEvent) {
    if (state.isAreaSelecting) return;
    console.log('[MAP_CLICK] mapClicked called', e.lngLat);

    // Synchronously check if editing is allowed (no modal)
    if (typeof window.canEditReference === 'function' && !window.canEditReference()) {
      console.log('[MAP_CLICK] Cannot edit - user needs to confirm referenced route edit first');
      // Show the warning modal
      if (typeof window.checkReferenceEditWarning === 'function') {
        const confirmed = await window.checkReferenceEditWarning();
        if (confirmed) {
          console.log('[MAP_CLICK] User confirmed - retrying map click');
          // Retry the click after confirmation
          return dispatch('mapClicked', e);
        }
      }
      return;
    }

    // Check if context menu is visible
    const contextMenuVisible = window.mapContextMenuVisible || false;
    if (contextMenuVisible) {
      console.log('[MAP_CLICK] Context menu visible, returning');
      return;
    }

    if (rootState.waypoints?.waypointAddingModeEnabled) {
      console.log('[MAP_CLICK] Waypoint adding mode enabled, returning');
      return;
    }

    const map = getMap();
    const rendered = map.queryRenderedFeatures(e.point);
    console.log('[MAP_CLICK] Rendered features:', rendered.length);

    // Check if clicking on a POI
    if (isClickOnPOIHelper(rendered)) {
      // Show POI details
      const poiFeature = rendered.find(f => f.source && f.source.includes('poi'));
      if (poiFeature && poiFeature.properties) {
        const { id, category_type, type, name } = poiFeature.properties as Record<string, any>;
        if (id && category_type && window.showPoi) {
          const coords =
            poiFeature.geometry && poiFeature.geometry.type === 'Point'
              ? (poiFeature.geometry.coordinates as [number, number])
              : [e.lngLat.lng, e.lngLat.lat];
          const seed = {
            id,
            type: type || category_type,
            name: name || null,
            lonlat: coords,
          };
          window.showPoi(category_type, id, seed);
        }
      }
      return;
    }

    if (isClickOnWaypointHelper(rendered)) return;

    // Check if clicking on a highlight
    const highlightFeatures = rendered.filter(f => f.layer.id.startsWith('highlight-'));
    if (highlightFeatures.length > 0) {
      return;
    }

    // Check if we're clicking on a route line using the hover layer for better hit detection
    // This matches the same wider detection area used for hover interactions
    const consolidatedHoverLayerIds = [
      "consolidated-active-routes-hover",
      "consolidated-routes-hover"
    ];

    const clickedLineFeature = rendered.find((feature) => {
      return consolidatedHoverLayerIds.includes(feature.layer.id);
    });
    if (clickedLineFeature) {

      const properties = clickedLineFeature.properties as {
        routeId: string;
        routeKey: string;
        lineIndex: number | string;
      };

      const routeId   = properties.routeId;
      const routeKey  = properties.routeKey;
      const lineIndex = Number(properties.lineIndex);   // defensive cast

      /* --- Defensive guards ------------------------------------------------- */
      if (!routeKey || !(routeKey in state.routes)) {
        console.warn("[mapClicked] Route key missing or unknown →", routeKey);
        return;
      }
      const route = state.routes[routeKey];

      const ls = route.lineStrings[lineIndex];
      if (!ls || !ls.lineStringGeometry || !Array.isArray(ls.lineStringGeometry.coordinates)) {
        console.warn("[mapClicked] LineString geometry missing for index", lineIndex);
        return;
      }
      /* ------------------------------------------------------------------ *
       * 2️⃣  Calculate the exact spot on the poly-line nearest to the click
       *     (pixel-accurate) and determine which segment we're on.
       * ------------------------------------------------------------------ */
      const clickCoord: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      const nearest = nearestPointOnLine(
        lineString(ls.lineStringGeometry.coordinates),
        point(clickCoord)
      );

      /* nearest.geometry.coordinates  ➜ the precise insert point           */
      /* nearest.properties.index      ➜ segment index inside the LS        */
      const hoverPoint = {
        lng: nearest.geometry.coordinates[0],
        lat: nearest.geometry.coordinates[1],
      };

      const segmentIndex = nearest.properties.index as number;

      /* Markers follow the rule: LineString[i] connects Marker[i]—Marker[i+1]   *
       * So the new marker belongs right after Marker[lineIndex]                 */
      const insertIndex = lineIndex + 1;

      /* ------------------------------------------------------------------ *
       * 3️⃣  Kick off your existing helper to insert the control point
       * ------------------------------------------------------------------ */
      await dispatch("insertMarkerAtPoint", {
        point: [hoverPoint.lng, hoverPoint.lat],
        routeId,
        insertIndex,
        segmentIndex,
      });
      return; // 🎉 handled the click
    }

    // Original click handling for adding markers at the end
    const features = rendered
    console.log('[MAP_CLICK] Checking for marker/polyline features');
    if (features.length > 0) {
      const markerFeatures = features.filter((feature) => feature.layer.id.includes("marker"));
      if (markerFeatures.length > 0) {
        const polylineFeatures = features.filter((feature) => feature.layer.id.includes("polyline"));
        if (polylineFeatures.length > 0) {
          console.log('[MAP_CLICK] Found marker and polyline, calling insertMarker');
          dispatch("insertMarker", {linestringFeature: polylineFeatures[0], markerFeature: markerFeatures[0]});
        }
        return;
      }
    }

    // Use the extracted action to add marker
    console.log('[MAP_CLICK] Calling addMarkerAtCoordinates');
    await dispatch('addMarkerAtCoordinates', { lng: e.lngLat.lng, lat: e.lngLat.lat });
    console.log('[MAP_CLICK] mapClicked complete');
  },

  async insertMarkerAtPoint({ state, commit, dispatch }: PlanTrailActionContext, { point, routeId, insertIndex, segmentIndex }: InsertPointData) {
    commit('setNearLine', false);
    commit('setHoverPoint', null);

    const routeKey = Object.keys(state.routes).find(key => state.routes[key].id === routeId);
    if (!routeKey) return;

    const route = state.routes[routeKey];
    if (!route) return;

    if (state.activeRouteKey !== routeKey) {
      commit('setActiveRoute', routeKey);
    }

    // Prevent inserting at the same coordinates as a neighbouring marker
    const prevMarker = insertIndex > 0 ? route.markers[insertIndex - 1] : null;
    const nextMarker = insertIndex < route.markers.length ? route.markers[insertIndex] : null;
    for (const neighbour of [prevMarker, nextMarker]) {
      if (!neighbour) continue;
      const dist = turfDistance(
        [neighbour.markerGeometry.coordinates[0], neighbour.markerGeometry.coordinates[1]],
        [point[0], point[1]],
        { units: 'meters' }
      );
      if (dist < MIN_SEGMENT_DISTANCE_METERS) return;
    }

    const markerId = generateID();
    const marker = new MarkerFeature(
      new MarkerGeometry([point[0], point[1]]),
      new MarkerProperties(markerId, state.isSnap, Date.now().toString(), 0)
    );

    const lineStringIndex = insertIndex - 1;

    // Capture original linestring BEFORE split for undo
    const originalLineString = lineStringIndex >= 0 && lineStringIndex < route.lineStrings.length
      ? route.lineStrings[lineStringIndex]
      : null;

    // Insert marker
    route.markers.splice(insertIndex, 0, marker);

    // Split the linestring
    let newLS1: LineStringFeature | null = null;
    let newLS2: LineStringFeature | null = null;

    if (lineStringIndex >= 0 &&
      lineStringIndex < route.lineStrings.length &&
      segmentIndex !== undefined) {

      const lineToSplit = route.lineStrings[lineStringIndex];
      const coords = lineToSplit.lineStringGeometry.coordinates;

      if (coords && coords.length >= 2) {
        const coords1: number[][] = [];
        const coords2: number[][] = [];

        for (let i = 0; i <= segmentIndex; i++) coords1.push(coords[i]);

        const insertCoord1: number[] =
          coords[0].length > 2 ? [point[0], point[1], 0] : [point[0], point[1]];
        const insertCoord2 = [...insertCoord1];
        coords1.push(insertCoord1);
        coords2.push(insertCoord2);

        for (let i = segmentIndex + 1; i < coords.length; i++) {
          coords2.push(coords[i]);
        }

        try {
          const prev = coords1[coords1.length - 2];
          const next = coords2[1];
          const elev = await getElevationData([
            [prev[1], prev[0]],
            [next[1], next[0]],
          ]);
          if (elev && elev.length === 2) {
            insertCoord1[2] = elev[0].elevation;
            insertCoord2[2] = elev[1].elevation;
          }
        } catch (e) {
          const z1 = coords1.length >= 2 ? (coords1[coords1.length - 2][2] ?? 0) : 0;
          const z2 = coords2.length >= 2 ? (coords2[1][2] ?? z1) : z1;
          const interp = (z1 + z2) / 2;
          insertCoord1[2] = insertCoord2[2] = interp;
        }

        if (coords1.length >= 2 && coords2.length >= 2) {
          newLS1 = LineStringFeature.coordsOnlyFeature(coords1);
          newLS2 = LineStringFeature.coordsOnlyFeature(coords2);
          newLS1.lineStringProperties = { ...lineToSplit.lineStringProperties, id: generateID() };
          newLS2.lineStringProperties = { ...lineToSplit.lineStringProperties, id: generateID() };
          route.lineStrings.splice(lineStringIndex, 1, newLS1, newLS2);
        }
      }
    }

    // Push undo command
    if (originalLineString && newLS1 && newLS2) {
      state.undoManager.push(new InsertMarkerCommand({
        routeKey,
        insertIndex,
        marker,
        originalLineStringIndex: lineStringIndex,
        originalLineString,
        newLineString1: newLS1,
        newLineString2: newLS2,
      }));
    }

    route.updated_at = new Date();
    await dispatch('updateRouteAndMarkers');
  },

  async insertMarker({ state, commit, dispatch, getters }: PlanTrailActionContext, data: InsertMarkerData) {
    const activeRoute = getters.activeRoute

    // Defensive: ensure coordinates are numbers and only [lng, lat]
    let polylineInsertPoint = data.markerFeature.geometry.coordinates;
    if (!Array.isArray(polylineInsertPoint) || polylineInsertPoint.length < 2) {
      throw new Error("Invalid marker coordinates");
    }
    polylineInsertPoint = [Number(polylineInsertPoint[0]), Number(polylineInsertPoint[1])];
    if (isNaN(polylineInsertPoint[0]) || isNaN(polylineInsertPoint[1])) {
      throw new Error("Marker coordinates are not numbers");
    }

    const polylineId = data.linestringFeature.source;
    const polylineIndex = parseInt(polylineId.split('-')[1]);
    const polylineFromState = activeRoute.lineStrings[polylineIndex];

    // Capture original linestring for undo before any modifications
    const originalLineString = polylineFromState ? JSON.parse(JSON.stringify(polylineFromState)) : null;

    // --- Add Validation Here ---
    if (!polylineFromState || !polylineFromState.lineStringGeometry || !Array.isArray(polylineFromState.lineStringGeometry.coordinates)) {
      console.error(`Invalid or missing linestring data in state for index: ${polylineIndex}`, polylineFromState);
      throw new Error(`Cannot insert marker: Invalid linestring data in state at index ${polylineIndex}.`);
    }

    const rawCoords = polylineFromState.lineStringGeometry.coordinates;
    const sanitizedCoords = rawCoords.map((coord, idx) => {
      if (!Array.isArray(coord) || coord.length < 2 || typeof coord[0] !== 'number' || typeof coord[1] !== 'number' || isNaN(coord[0]) || isNaN(coord[1])) {
        console.warn(`Invalid coordinate found in linestring ${polylineIndex} at index ${idx}:`, coord);
        return null; // Mark for removal or handling
      }
      // Optionally handle elevation: ensure it's a number if present
      if (coord.length > 2 && (typeof coord[2] !== 'number' || isNaN(coord[2]))) {
        console.warn(`Invalid elevation found in linestring ${polylineIndex} at index ${idx}, removing:`, coord[2]);
        return [coord[0], coord[1]]; // Return only valid lng/lat
      }
      return coord; // Return original valid coordinate
    }).filter(coord => coord !== null); // Remove nulls

    if (sanitizedCoords.length < 2) {
      console.error(`Linestring ${polylineIndex} has less than 2 valid coordinates after sanitization:`, rawCoords);
      throw new Error(`Cannot insert marker: Linestring ${polylineIndex} has insufficient valid coordinates.`);
    }
    // --- End Validation ---

    // Use the sanitized coordinates
    const uniqueCoords = cleanCoords(
      lineString(sanitizedCoords),   // <-- turf feature
      { mutate: false }              // keep original untouched
    ).geometry.coordinates;          // grab the clean array back

    const linestring = lineString(uniqueCoords);   // safe to use now
    const markerPoint = point(polylineInsertPoint);

    const nearest = nearestPointOnLine(linestring, markerPoint);
    const result = lineSplit(linestring, nearest);

    const lineString1 = result.features[0];
    const coords1 = lineString1.geometry.coordinates;
    const lineStringFeature1 = LineStringFeature.coordsOnlyFeature(coords1);
    const coords1ToGetElev = coords1[coords1.length - 1];

    const lineString2 = result.features[1];
    const coords2 = lineString2.geometry.coordinates;
    const lineStringFeature2 = LineStringFeature.coordsOnlyFeature(coords2);
    const coords2ToGetElev = coords2[0];

    const elevation = await getElevationData([[coords1ToGetElev[1], coords1ToGetElev[0]], [coords2ToGetElev[1], coords2ToGetElev[0]]]);
    lineStringFeature1.lineStringGeometry.coordinates[coords1.length - 1][2] = elevation[0].elevation;
    lineStringFeature2.lineStringGeometry.coordinates[0][2] = elevation[1].elevation;

    commit('removeLineStringAtIndex', polylineIndex);
    commit('addLineStringAtIndex', {lineString: lineStringFeature1, index: polylineIndex});
    commit('addLineStringAtIndex', {lineString: lineStringFeature2, index: polylineIndex + 1});

    const shouldBeSnap = activeRoute.markers[polylineIndex + 1].markerProperties.isSnap;
    const id = generateID();
    commit('insertMarkerObj', {
      lng: markerPoint.geometry.coordinates[0],
      lat: markerPoint.geometry.coordinates[1],
      id: id,
      isSnap: shouldBeSnap,
      index: polylineIndex + 1
    });

    // Push undo command
    if (originalLineString) {
      const insertedMarker = activeRoute.markers[polylineIndex + 1];
      state.undoManager.push(new InsertMarkerCommand({
        routeKey: state.activeRouteKey!,
        insertIndex: polylineIndex + 1,
        marker: insertedMarker,
        originalLineStringIndex: polylineIndex,
        originalLineString,
        newLineString1: lineStringFeature1,
        newLineString2: lineStringFeature2,
      }));
    }

    // Refresh map display — update routes and markers only
    await dispatch('updateRouteAndMarkers');
  },
};
